import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import timeIcon from '../assets/images/Formicons/time_duration.svg';
import SliderPin from "../assets/images/Formicons/SliderPin.svg"
import { ShimmerBox } from '../components/Shimmer';
import '../css/Shimmer.css';
import '../css/ServiceSettings.css';
import {
  getServiceVariants,
  updateServiceVariants,
  getServiceAvailability,
  saveServiceAvailability,
  getServiceProfile,
  updateServiceProfile,
  getServiceProfileImages,
  uploadServiceProfileImage,
  deleteServiceProfileImage,
  getHolidayUplift,
  saveHolidayUplift,
  clearServiceSettings,
  getBaseLocationDetail
} from '../features/serviceLocations/serviceLocationSlice';

const DAY_ABBREV = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Data mappers ─────────────────────────────────────────────────────────
const mapVariant = (v) => ({
  id:          v.SubServiceId,
  name:        v.Name,
  description: v.Description || '',
  price:       v.EffectivePrice ?? v.BaseCustomPrice ?? v.AdminPrice ?? '',
  duration:    (v.EffectiveDuration ?? '').replace(/\s?min/i, '').trim(),
  isActive:    v.IsEnabled ?? v.IsEnabledAtBase ?? true,
  features:   (v.Features || []).map((f) => ({ id: f.FeatureId, label: f.Name, checked: f.IsIncluded })),
  warranty:    v.OtherFacilities?.Warranty || '',
  pickup:      v.OtherFacilities?.PickupDrop === true ? 'yes' : v.OtherFacilities?.PickupDrop === false ? 'no' : '',
});

const mapScheduleDay = (d) => ({
  id:       d.DayOfWeek,
  day:      DAY_ABBREV[d.DayOfWeek] ?? d.Day.slice(0, 3),
  fullDay:  d.Day,
  isActive: d.IsActive,
  slots:    d.Slots?.length > 0
    ? d.Slots.map((s) => ({ id: s.Id, from: s.StartTime, to: s.EndTime }))
    : [{ id: null, from: '--:--', to: '--:--' }],
});

const mapHoliday = (h) => ({
  holidayId:        h.HolidayId,
  name:             h.HolidayName,
  dateDisplay:      h.DateDisplay,
  startTime:        h.StartTime,
  endTime:          h.EndTime,
  upliftPercentage: h.UpliftPercentage,
  isActive:         h.IsActive,
  isCustomized:     h.IsCustomized,
  _origStart:       h.StartTime,
  _origEnd:         h.EndTime,
  _origUplift:      h.UpliftPercentage,
  _origActive:      h.IsActive,
});

const buildVariantsPayload = (variants, radius) => ({
  RadiusMiles: radius,
  Variants: variants.map((v) => ({
    SubServiceId:       v.id,
    IsEnabled:          v.isActive,
    CustomPrice:        v.price    ? String(v.price)    : null,
    CustomDuration:     v.duration ? String(v.duration) : null,
    IncludedFeatureIds: v.features.filter((f) => f.checked).map((f) => f.id),
    Warranty:           v.warranty || null,
    PickupDrop:         v.pickup === 'yes' ? true : v.pickup === 'no' ? false : null,
  })),
});

const buildAvailabilityPayload = (availability) => ({
  Schedule: availability.map((d) => ({
    Day:      d.id,
    IsActive: d.isActive,
    Slots:    d.isActive
      ? d.slots.filter((s) => s.from !== '--:--' && s.to !== '--:--')
               .map((s) => ({ AvailableFrom: s.from, AvailableTo: s.to }))
      : [],
  })),
});

const getDirtyHolidays = (holidays) =>
  holidays.filter(
    (h) =>
      h.startTime        !== h._origStart   ||
      h.endTime          !== h._origEnd     ||
      h.upliftPercentage !== h._origUplift  ||
      h.isActive         !== h._origActive
  );

const TIME_OPTIONS = (() => {
  const t = [];
  for (let h = 0; h < 24; h++)
    for (let m = 0; m < 60; m += 30)
      t.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  return t;
})();

// ─── Small reusable components ────────────────────────────────────────────

const TimeDropdown = ({ value, pickerId, activeId, onOpen, onSelect, dropdownRef }) => (
  <div className="service-settings-time-input-wrapper">
    <div
      className="service-settings-time-input service-settings-time-input-reverse"
      onClick={() => onOpen(activeId === pickerId ? null : pickerId)}
    >
      <span>{value || '--:--'}</span>
      <img src={timeIcon} alt="Time" className="service-settings-time-icon" />
    </div>
    {activeId === pickerId && (
      <div className="service-settings-time-dropdown" ref={dropdownRef}>
        {TIME_OPTIONS.map((t) => (
          <div
            key={t}
            className={`service-settings-time-option ${value === t ? 'selected' : ''}`}
            onClick={() => onSelect(t)}
          >
            {t}
          </div>
        ))}
      </div>
    )}
  </div>
);

const VariantsSkeleton = () => (
  <div>
    {/* Warning banner */}
    <ShimmerBox width="100%" height="48px" borderRadius="10px" style={{ marginBottom: 20 }} />

    {/* Radius card */}
    <div className="ss-content-card2" style={{ marginBottom: 20 }}>
      <ShimmerBox width="140px" height="17px" borderRadius="6px" />
      <ShimmerBox width="260px" height="13px" borderRadius="6px" style={{ marginTop: 8, marginBottom: 20 }} />
      <ShimmerBox width="100%" height="6px" borderRadius="3px" />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <ShimmerBox width="50px" height="13px" borderRadius="6px" />
        <ShimmerBox width="50px" height="13px" borderRadius="6px" />
      </div>
    </div>

    {/* Variants table */}
    <div className="service-settings-content">
      <div className="ss-variants-header">
        <span className="ss-vth ss-vth-cat">Category</span>
        <span className="ss-vth ss-vth-price">Price</span>
        <span className="ss-vth ss-vth-dur">Duration</span>
        <span className="ss-vth ss-vth-action">Action</span>
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="ss-variant-block">
          <div className="ss-variant-row">
            <div className="ss-variant-cat" style={{ gap: 12 }}>
              <ShimmerBox width="52px" height="28px" borderRadius="14px" />
              <div>
                <ShimmerBox width="200px" height="16px" />
                <ShimmerBox width="140px" height="13px" style={{ marginTop: 6 }} />
              </div>
            </div>
            <div className="ss-variant-price-col"><ShimmerBox width="70px" height="40px" borderRadius="6px" /></div>
            <div className="ss-variant-dur-col"><ShimmerBox width="70px" height="40px" borderRadius="6px" /></div>
            <div className="ss-variant-action-col"><ShimmerBox width="36px" height="36px" borderRadius="8px" /></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AvailabilitySkeleton = () => (
  <div className="ss-avail-shimmer-scope">
    {/* Warning banner */}
    <ShimmerBox width="100%" height="48px" borderRadius="10px" style={{ marginBottom: 20 }} />

    <div className="service-settings-content">
      {/* Fake thead */}
      <div style={{ display: 'flex', gap: 0, padding: '20px 24px', borderBottom: '1px solid #F1F5F9', background: 'linear-gradient(90deg,#EFEAFF,#F8F9FF)' }}>
        <ShimmerBox width="80px"  height="16px" borderRadius="6px" style={{ marginRight: 60 }} />
        <ShimmerBox width="120px" height="16px" borderRadius="6px" style={{ marginRight: 60 }} />
        <ShimmerBox width="120px" height="16px" borderRadius="6px" style={{ marginRight: 'auto' }} />
        <ShimmerBox width="60px"  height="16px" borderRadius="6px" />
      </div>

      {[1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '22px 24px', borderBottom: '1px solid #F1F5F9' }}>
          {/* Day */}
          <ShimmerBox width="40px" height="20px" borderRadius="6px" style={{ flexShrink: 0 }} />
          {/* From time input */}
          <ShimmerBox width="120px" height="40px" borderRadius="6px" style={{ flexShrink: 0 }} />
          {/* "To" separator */}
          <ShimmerBox width="24px" height="14px" borderRadius="4px" style={{ flexShrink: 0 }} />
          {/* To time input */}
          <ShimmerBox width="120px" height="40px" borderRadius="6px" style={{ flexShrink: 0 }} />
          {/* Add slot circle */}
          <ShimmerBox width="36px" height="36px" borderRadius="50%" style={{ flexShrink: 0 }} />
          {/* Toggle — pushed right */}
          <div style={{ marginLeft: 'auto' }}>
            <ShimmerBox width="52px" height="28px" borderRadius="14px" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const HolidaySkeleton = () => (
  <div className="service-settings-content">
    {/* Fake thead */}
    <div style={{ display: 'flex', gap: 40, padding: '20px 24px', borderBottom: '1px solid #F1F5F9', background: 'linear-gradient(90deg,#EFEAFF,#F8F9FF)' }}>
      <ShimmerBox width="100px" height="16px" borderRadius="6px" />
      <ShimmerBox width="90px"  height="16px" borderRadius="6px" />
      <ShimmerBox width="90px"  height="16px" borderRadius="6px" />
      <ShimmerBox width="80px"  height="16px" borderRadius="6px" />
    </div>

    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '22px 24px', borderBottom: '1px solid #F1F5F9' }}>
        {/* Date column: name + sub-date */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <ShimmerBox width="160px" height="18px" borderRadius="6px" />
          <ShimmerBox width="90px"  height="14px" borderRadius="6px" />
        </div>
        {/* Start time */}
        <ShimmerBox width="130px" height="40px" borderRadius="6px" style={{ flexShrink: 0 }} />
        {/* End time (with "To" chip) */}
        <ShimmerBox width="30px"  height="14px" borderRadius="4px" style={{ flexShrink: 0 }} />
        <ShimmerBox width="130px" height="40px" borderRadius="6px" style={{ flexShrink: 0 }} />
        {/* Increase % */}
        <ShimmerBox width="110px" height="40px" borderRadius="6px" style={{ flexShrink: 0 }} />
      </div>
    ))}
  </div>
);

const ProfileSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} className="ss-profile-shimmer-scope">
    {/* Warning banner */}
    <ShimmerBox width="100%" height="48px" borderRadius="10px" />

    {/* Description card */}
    <div className="ss-content-card">
      <div className="ss-card-header" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ShimmerBox width="160px" height="17px" borderRadius="6px" />
        <ShimmerBox width="280px" height="13px" borderRadius="6px" />
      </div>
      <div style={{ padding: '16px 32px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <ShimmerBox width="20px" height="20px" borderRadius="4px" style={{ flexShrink: 0, marginTop: 2 }} />
        <ShimmerBox width="100%" height="72px" borderRadius="8px" />
      </div>
    </div>

    {/* Gallery card */}
    <div className="ss-content-card">
      <div className="ss-card-header" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ShimmerBox width="130px" height="17px" borderRadius="6px" />
        <ShimmerBox width="300px" height="13px" borderRadius="6px" />
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: 32 }}>
        {[1, 2, 3].map((i) => (
          <ShimmerBox key={i} width="200px" height="140px" borderRadius="10px" />
        ))}
      </div>
      <ShimmerBox width="140px" height="44px" borderRadius="10px" style={{ margin: '0 0 4px 32px' }} />
    </div>

    {/* Badges card */}
    <div className="ss-content-card">
      <div className="ss-card-header" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <ShimmerBox width="170px" height="17px" borderRadius="6px" />
        <ShimmerBox width="320px" height="13px" borderRadius="6px" />
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: 32 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <ShimmerBox key={i} width="100px" height="36px" borderRadius="8px" />
        ))}
      </div>
    </div>
  </div>
);

const DeleteImageModal = ({ onConfirm, onCancel, isLoading }) => (
  <div className="sd-modal-overlay" onClick={onCancel}>
    <div className="sd-confirm-modal" onClick={(e) => e.stopPropagation()}>
      <div className="sd-confirm-header">
        <button className="sd-confirm-close" onClick={onCancel}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 3L13 13M13 3L3 13" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <div className="sd-confirm-body">
        <h4 className="sd-confirm-title">Delete Image</h4>
        <p className="sd-confirm-message">Are you sure you want to remove this image?</p>
      </div>
      <div className="sd-confirm-footer">
        <button className="sd-confirm-btn sd-confirm-btn--outline" onClick={onCancel} disabled={isLoading}>CANCEL</button>
        <button className="sd-confirm-btn sd-confirm-btn--disable" onClick={onConfirm} disabled={isLoading}>{isLoading ? '...' : 'DELETE'}</button>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────
const ServiceSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { baseId, serviceId } = useParams();

  const {
    currentBase, currentServiceVariants, currentServiceAvailability,
    currentServiceProfile, serviceProfileImages,
    holidayUplift, isLoadingHoliday, isSavingHoliday, holidayError,
    isLoadingVariants, isLoadingAvailability, isLoadingProfile,
    isSavingVariants, isSavingAvailability, isSavingProfile,
    isUploadingImage, isDeletingImage,
    variantsError, availabilityError, profileError,
  } = useSelector((state) => state.serviceLocations);

  // ── Navigation ─────────────────────────────────────────────────────────
  const [topTab,       setTopTab]       = useState('all');
  const [allSubTab,    setAllSubTab]    = useState('details');
  const [garageSubTab, setGarageSubTab] = useState('variants');

  // Add this state near the other "For All Locations" state:
const [imageLimits, setImageLimits] = useState({ DescriptionMaxLength: 500, ImageMin: 1, ImageMax: 6 });
const [canAddMore,  setCanAddMore]  = useState(true);
const [canDelete,   setCanDelete]   = useState(true);

  // ── Mobile detection ───────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── For All Locations state ────────────────────────────────────────────
  const [description,       setDescription]      = useState('');
  const [sectionTitle,      setSectionTitle]      = useState('');
  const [selectedBadges,    setSelectedBadges]    = useState([]);
  const [allBadges,         setAllBadges]         = useState([]);
  const [deleteImageTarget, setDeleteImageTarget] = useState(null);
  const [holidays,          setHolidays]          = useState([]);

  // ── Garage state ───────────────────────────────────────────────────────
  const [radius,          setRadius]          = useState(5);
  const [variants,        setVariants]        = useState([]);
  const [expandedId,      setExpandedId]      = useState(null);
  const [warrantyOptions, setWarrantyOptions] = useState([]);
  const [availability,    setAvailability]    = useState([]);

  const availabilityFetchedRef = useRef(false);
  const holidayFetchedRef      = useRef(false);

  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const timePickerRef = useRef(null);
  const fileInputRef  = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Outside click → close time picker ─────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (timePickerRef.current && !timePickerRef.current.contains(e.target))
        setActiveTimePicker(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Fetch on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    if (baseId && serviceId) {
      dispatch(getServiceVariants({ baseId, serviceId }));
      dispatch(getServiceProfile(serviceId));
      dispatch(getServiceProfileImages(serviceId));
      if (!currentBase) dispatch(getBaseLocationDetail(baseId));
    }
    return () => { dispatch(clearServiceSettings()); };
  }, [baseId, serviceId, dispatch]);

  // ── Sync variants ──────────────────────────────────────────────────────
  useEffect(() => {
    if (currentServiceVariants) {
      setRadius(currentServiceVariants.RadiusMiles ?? 5);
      const raw = currentServiceVariants.Variants || [];
      setVariants(raw.map(mapVariant));
      setWarrantyOptions(currentServiceVariants.WarrantyOptions || []);
      if (raw.length > 0) setExpandedId(raw[0].SubServiceId);
    }
  }, [currentServiceVariants]);

  // ── Sync profile ───────────────────────────────────────────────────────
useEffect(() => {
  if (currentServiceProfile) {
    setDescription(currentServiceProfile.ActiveDescription || currentServiceProfile.Description || '');
    setSectionTitle(currentServiceProfile.SectionTitle || '');
    setAllBadges(currentServiceProfile.Badges || []);
    setSelectedBadges((currentServiceProfile.Badges || []).filter((b) => b.IsSelected).map((b) => b.BadgeId));

    // ── NEW ──
    if (currentServiceProfile.Limits) {
      setImageLimits(currentServiceProfile.Limits);
    }
    const count = currentServiceProfile.ImageCount ?? (currentServiceProfile.Images?.length ?? 0);
    const max   = currentServiceProfile.Limits?.ImageMax ?? 6;
    const min   = currentServiceProfile.Limits?.ImageMin ?? 1;
    setCanAddMore(count < max);
    setCanDelete(count > min);
  }
}, [currentServiceProfile]);

  // ── Lazy fetch availability ────────────────────────────────────────────
  useEffect(() => {
    if (topTab === 'garage' && garageSubTab === 'availability' && !availabilityFetchedRef.current) {
      availabilityFetchedRef.current = true;
      dispatch(getServiceAvailability({ baseId, serviceId }));
    }
  }, [topTab, garageSubTab, baseId, serviceId, dispatch]);

  // ── Lazy fetch holiday uplift ──────────────────────────────────────────
  useEffect(() => {
    if (topTab === 'all' && allSubTab === 'holiday' && !holidayFetchedRef.current) {
      holidayFetchedRef.current = true;
      dispatch(getHolidayUplift(serviceId));
    }
  }, [topTab, allSubTab, serviceId, dispatch]);

  // ── Sync availability ──────────────────────────────────────────────────
  useEffect(() => {
    if (currentServiceAvailability?.Schedule) {
      setAvailability(currentServiceAvailability.Schedule.map(mapScheduleDay));
    }
  }, [currentServiceAvailability]);

  // ── Sync holiday uplift ────────────────────────────────────────────────
  useEffect(() => {
    if (holidayUplift?.Holidays) {
      setHolidays(holidayUplift.Holidays.map(mapHoliday));
    }
  }, [holidayUplift]);

  // ── Error toasts ───────────────────────────────────────────────────────
  useEffect(() => { if (variantsError)     showToast('error', variantsError);     }, [variantsError,     showToast]);
  useEffect(() => { if (availabilityError) showToast('error', availabilityError); }, [availabilityError, showToast]);
  useEffect(() => { if (profileError)      showToast('error', profileError);      }, [profileError,      showToast]);
  useEffect(() => { if (holidayError)      showToast('error', holidayError);      }, [holidayError,      showToast]);

  // ── Derived values ─────────────────────────────────────────────────────
  const serviceFromBase = currentBase?.Services?.find((s) => String(s.ServiceId) === String(serviceId));
  const serviceName = serviceFromBase?.ServiceName ?? currentServiceVariants?.ServiceName ?? currentServiceProfile?.ServiceName ?? 'Service';
  const serviceIcon = serviceFromBase?.Icon        ?? null;
  const serviceDesc = serviceFromBase?.Description ?? currentServiceProfile?.Description ?? '';
  const baseName    = currentBase?.BaseName        ?? currentServiceVariants?.BaseName    ?? 'This Location';

  // ── Badge toggle (max 3) ───────────────────────────────────────────────
  const toggleBadge = (id) =>
    setSelectedBadges((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : prev.length < 3 ? [...prev, id] : prev
    );

  // ── Holiday handlers ───────────────────────────────────────────────────
  const changeHolidayField = (holidayId, field, value) =>
    setHolidays((prev) => prev.map((h) => h.holidayId === holidayId ? { ...h, [field]: value } : h));

  const changeHolidayTime = (holidayId, field, value) => {
    changeHolidayField(holidayId, field, value);
    setActiveTimePicker(null);
  };

  // ── Variant handlers ───────────────────────────────────────────────────
  const toggleVariant  = (id)       => setVariants((p) => p.map((v) => v.id === id ? { ...v, isActive: !v.isActive } : v));
  const changePrice    = (id, val)  => setVariants((p) => p.map((v) => v.id === id ? { ...v, price: val } : v));
  const changeDuration = (id, val)  => setVariants((p) => p.map((v) => v.id === id ? { ...v, duration: val } : v));
  const toggleFeature  = (vid, fid) => setVariants((p) => p.map((v) => v.id === vid
    ? { ...v, features: v.features.map((f) => f.id === fid ? { ...f, checked: !f.checked } : f) } : v));
  const changeWarranty = (id, val)  => setVariants((p) => p.map((v) => v.id === id ? { ...v, warranty: val } : v));
  const changePickup   = (id, val)  => setVariants((p) => p.map((v) => v.id === id ? { ...v, pickup: val } : v));
  const toggleExpand   = (id)       => setExpandedId((prev) => prev === id ? null : id);

  // ── Availability handlers ──────────────────────────────────────────────
  const toggleAvailDay = (id) =>
    setAvailability((p) => p.map((d) => d.id === id ? { ...d, isActive: !d.isActive } : d));

  const setSlotTime = (dayId, idx, field, val) => {
    setAvailability((p) => p.map((d) => {
      if (d.id !== dayId) return d;
      const slots = [...d.slots];
      slots[idx] = { ...slots[idx], [field]: val };
      return { ...d, slots };
    }));
    setActiveTimePicker(null);
  };

  const addSlot    = (dayId) => setAvailability((p) => p.map((d) =>
    d.id === dayId ? { ...d, slots: [...d.slots, { id: null, from: '--:--', to: '--:--' }] } : d
  ));
  const removeSlot = (dayId, idx) => setAvailability((p) => p.map((d) =>
    d.id === dayId && d.slots.length > 1 ? { ...d, slots: d.slots.filter((_, i) => i !== idx) } : d
  ));

  // ── Image handlers ─────────────────────────────────────────────────────
 // Replace handleImageUpload:
const handleImageUpload = async (e) => {
  const files = Array.from(e.target.files);
  if (!files.length) return;
  for (const file of files) {
    const formData = new FormData();
    formData.append('Image', file);
    const result = await dispatch(uploadServiceProfileImage({ serviceId, formData }));
    if (uploadServiceProfileImage.fulfilled.match(result)) {
      showToast('success', `${file.name} uploaded.`);
      // ── NEW: use helpers from POST response ──
      if (result.payload?.CanAddMore !== undefined) setCanAddMore(result.payload.CanAddMore);
      if (result.payload?.CanDelete  !== undefined) setCanDelete(result.payload.CanDelete);
    }
  }
  e.target.value = '';
};

  // Replace handleDeleteImage:
const handleDeleteImage = async () => {
  if (!deleteImageTarget) return;
  const result = await dispatch(deleteServiceProfileImage({ serviceId, imageId: deleteImageTarget.Id }));
  if (deleteServiceProfileImage.fulfilled.match(result)) {
    setDeleteImageTarget(null);
    showToast('success', 'Image deleted.');
    // ── NEW: recalculate from remaining count ──
    const remaining = serviceProfileImages.length - 1;
    setCanAddMore(remaining < imageLimits.ImageMax);
    setCanDelete(remaining > imageLimits.ImageMin);
  }
};

  // ── Save handlers ──────────────────────────────────────────────────────
  const handleSaveHolidays = async () => {
    const dirty = getDirtyHolidays(holidays);
    if (dirty.length === 0) { showToast('success', 'No changes to save.'); return; }

    let successCount = 0;
    for (const h of dirty) {
      const result = await dispatch(saveHolidayUplift({
        serviceId,
        data: {
          HolidayId:        h.holidayId,
          StartTime:        h.startTime,
          EndTime:          h.endTime,
          UpliftPercentage: Number(h.upliftPercentage),
          IsActive:         h.isActive,
        },
      }));
      if (saveHolidayUplift.fulfilled.match(result)) {
        successCount++;
        setHolidays((prev) => prev.map((hh) =>
          hh.holidayId === h.holidayId
            ? { ...hh, _origStart: hh.startTime, _origEnd: hh.endTime, _origUplift: hh.upliftPercentage, _origActive: hh.isActive }
            : hh
        ));
      }
    }
    if (successCount === dirty.length)
      showToast('success', `${successCount} holiday uplift${successCount > 1 ? 's' : ''} saved.`);
    else
      showToast('error', `${successCount}/${dirty.length} holidays saved. Check console for errors.`);
  };

  const handleSave = async () => {
    if (topTab === 'all' && allSubTab === 'details') {
      const r = await dispatch(updateServiceProfile({ serviceId, data: { Description: description, SectionTitle: sectionTitle, BadgeIds: selectedBadges } }));
      if (updateServiceProfile.fulfilled.match(r)) showToast('success', 'Service details updated.');
    } else if (topTab === 'all' && allSubTab === 'holiday') {
      await handleSaveHolidays();
    } else if (topTab === 'garage' && garageSubTab === 'variants') {
      const r = await dispatch(updateServiceVariants({ baseId, serviceId, data: buildVariantsPayload(variants, radius) }));
      if (updateServiceVariants.fulfilled.match(r)) showToast('success', `${serviceName} variants updated.`);
    } else if (topTab === 'garage' && garageSubTab === 'availability') {
      const r = await dispatch(saveServiceAvailability({ baseId, serviceId, data: buildAvailabilityPayload(availability) }));
      if (saveServiceAvailability.fulfilled.match(r)) {
        showToast('success', 'Availability saved.');
        availabilityFetchedRef.current = false;
        dispatch(getServiceAvailability({ baseId, serviceId }));
      }
    }
  };

  const isSaving = isSavingProfile || isSavingVariants || isSavingAvailability || isSavingHoliday;

  // ── Mobile Availability Cards ──────────────────────────────────────────
  const renderMobileAvailability = () => (
    <div className="ss-avail-mobile service-settings-content">
      {availability.map((day) => (
        <div key={day.id} className="ss-avail-day-card">
          <div className="ss-avail-day-header">
            <span className="service-settings-day-name">{day.day}</span>
            <label className="service-settings-toggle">
              <input type="checkbox" checked={day.isActive} onChange={() => toggleAvailDay(day.id)} />
              <span className="service-settings-toggle-slider" />
            </label>
          </div>

          {day.slots.map((slot, idx) => (
            <div key={idx} className="ss-avail-slot-row">
              {/* From */}
              <TimeDropdown
                value={slot.from}
                pickerId={`avail-${day.id}-${idx}-from`}
                activeId={activeTimePicker}
                onOpen={setActiveTimePicker}
                onSelect={(val) => setSlotTime(day.id, idx, 'from', val)}
                dropdownRef={timePickerRef}
              />

              <span className="service-settings-time-separator">→</span>

              {/* To */}
              <TimeDropdown
                value={slot.to}
                pickerId={`avail-${day.id}-${idx}-to`}
                activeId={activeTimePicker}
                onOpen={setActiveTimePicker}
                onSelect={(val) => setSlotTime(day.id, idx, 'to', val)}
                dropdownRef={timePickerRef}
              />

              {/* Add / Remove */}
              {idx === day.slots.length - 1
                ? <button className="service-settings-add-slot-btn" onClick={() => addSlot(day.id)}>+</button>
                : <button className="service-settings-remove-slot-btn" onClick={() => removeSlot(day.id, idx)}>×</button>
              }
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // ── Mobile Holiday Cards ───────────────────────────────────────────────
  const renderMobileHoliday = () => (
    <div className="ss-holiday-mobile service-settings-content">
      {holidays.map((h) => {
        const isDirty =
          h.startTime        !== h._origStart  ||
          h.endTime          !== h._origEnd    ||
          h.upliftPercentage !== h._origUplift ||
          h.isActive         !== h._origActive;

        return (
          <div
            key={h.holidayId}
            className="ss-holiday-card"
            style={{ background: isDirty ? '#FFFBF0' : undefined }}
          >
            <div className="ss-holiday-card-name">{h.name}</div>
            <div className="ss-holiday-card-date">({h.dateDisplay})</div>

            {/* From + To side by side */}
            <div className="ss-holiday-time-pair">
              <div className="ss-holiday-time-field">
                <span className="ss-holiday-time-field-label">Start From</span>
                <TimeDropdown
                  value={h.startTime}
                  pickerId={`hol-${h.holidayId}-start`}
                  activeId={activeTimePicker}
                  onOpen={setActiveTimePicker}
                  onSelect={(val) => changeHolidayTime(h.holidayId, 'startTime', val)}
                  dropdownRef={timePickerRef}
                />
              </div>

              <div className="ss-holiday-time-field">
                <span className="ss-holiday-time-field-label">End Time</span>
                <TimeDropdown
                  value={h.endTime}
                  pickerId={`hol-${h.holidayId}-end`}
                  activeId={activeTimePicker}
                  onOpen={setActiveTimePicker}
                  onSelect={(val) => changeHolidayTime(h.holidayId, 'endTime', val)}
                  dropdownRef={timePickerRef}
                />
              </div>
            </div>

            {/* Uplift % */}
            <div className="ss-holiday-uplift-row">
              <span className="ss-holiday-uplift-label">Increase</span>
              <div className="service-settings-increase-wrapper">
                <input
                  type="number"
                  value={h.upliftPercentage}
                  onChange={(e) => changeHolidayField(h.holidayId, 'upliftPercentage', e.target.value)}
                  className="service-settings-increase-input"
                />
                <span className="service-settings-increase-percent">%</span>
                <span className="service-settings-increase-icon">↑</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div className="service-settings-page">
      {/* Toast */}
      {toast && (
        <div className={`sd-toast sd-toast--${toast.type}`}>
          {toast.type === 'success'
            ? <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" fill="#10B981" opacity="0.15" stroke="#10B981" strokeWidth="1.5"/><path d="M5 9l3 3 5-5" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            : <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" fill="#EF4444" opacity="0.15" stroke="#EF4444" strokeWidth="1.5"/><path d="M6 6l6 6M12 6l-6 6" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round"/></svg>
          }
          <span>{toast.msg}</span>
        </div>
      )}

      <Container fluid className="px-4 py-4">

        {/* Service Header */}
        <div className="service-settings-header">
          <div className="service-settings-icon">
            {serviceIcon
              ? <img src={serviceIcon} alt={serviceName} />
              : <div className="service-settings-icon-placeholder" />
            }
          </div>
          <div className="service-settings-info">
            <h1 className="service-settings-name">{serviceName}</h1>
            <p className="service-settings-description">{serviceDesc}</p>
          </div>
        </div>

        {/* Top Toggle */}
        <div className="ss-scope-toggle">
          <button className={`ss-scope-btn ${topTab === 'all'    ? 'active' : ''}`} onClick={() => setTopTab('all')}>For All Locations</button>
          <button className={`ss-scope-btn ${topTab === 'garage' ? 'active' : ''}`} onClick={() => setTopTab('garage')}>For {baseName}</button>
        </div>

        {/* Sub Tabs */}
        <div className="ss-sub-tabs">
          {topTab === 'all' ? (
            <>
              <button className={`ss-sub-tab ${allSubTab === 'details' ? 'active' : ''}`} onClick={() => setAllSubTab('details')}>Service Details</button>
              <button className={`ss-sub-tab ${allSubTab === 'holiday' ? 'active' : ''}`} onClick={() => setAllSubTab('holiday')}>Holiday Uplift</button>
            </>
          ) : (
            <>
              <button className={`ss-sub-tab ${garageSubTab === 'variants'     ? 'active' : ''}`} onClick={() => setGarageSubTab('variants')}>Variants</button>
              <button className={`ss-sub-tab ${garageSubTab === 'availability' ? 'active' : ''}`} onClick={() => setGarageSubTab('availability')}>Availability</button>
            </>
          )}
        </div>

        {/* ══ FOR ALL LOCATIONS — SERVICE DETAILS ══ */}
        {topTab === 'all' && allSubTab === 'details' && (
          isLoadingProfile ? <ProfileSkeleton /> : (
            <>
              <div className="ss-warning-banner">
                <span className="ss-warning-icon">⚠️</span>
                Changes here apply to this service across all your locations.
              </div>

              <div className="ss-content-card">
                <div className="ss-card-header">
                  <h3 className="ss-card-title">Service Description</h3>
                  <p className="ss-card-sub">This appears on the customer app below your service name</p>
                </div>
                <div className="ss-desc-wrap">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="ss-desc-icon"><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V8l-6-5z" stroke="#94a3b8" strokeWidth="1.6" fill="none"/><path d="M14 3v5h5M8 13h8M8 17h5" stroke="#94a3b8" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
    <textarea
      className="ss-desc-textarea"
      placeholder="Add Service tagline/description"
      value={description}
      onChange={(e) => {
        if (e.target.value.length <= imageLimits.DescriptionMaxLength)
          setDescription(e.target.value);
      }}
      rows={3}
    />
    <span style={{
      fontFamily: '"DM Sans"', fontSize: 12,
      color: description.length >= imageLimits.DescriptionMaxLength ? '#EF4444' : '#94a3b8',
      textAlign: 'right'
    }}>
      {description.length}/{imageLimits.DescriptionMaxLength}
    </span>
  </div>
                </div>
              </div>

              <div className="ss-content-card">
                <div className="ss-card-header">
                  <h3 className="ss-card-title">Gallery Images</h3>
                  <p className="ss-card-sub">These photos appears at the top of your service card (max 6)</p>
                </div>
                <div className="ss-gallery-grid">
                  {serviceProfileImages.map((img) => (
                    <div key={img.Id} className="ss-gallery-img-wrap" style={{ position: 'relative' }}>
                      <img src={img.ImageUrl} alt={img.Caption || 'Gallery'} className="ss-gallery-img" />
                      <button
                        onClick={() => {
    if (!canDelete) {
      showToast('error', `Minimum ${imageLimits.ImageMin} image required. Upload a new image before deleting this one.`);
      return;
    }
    setDeleteImageTarget(img);
  }}
                        style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 14, lineHeight: 1, opacity: canDelete ? 1 : 0.4 }}
                        title={canDelete ? 'Delete image' : 'Minimum 1 image required'}
                      >×</button>
                    </div>
                  ))}
                </div>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" multiple onChange={handleImageUpload} />
                {canAddMore && (
  <button className="ss-upload-btn" onClick={() => fileInputRef.current?.click()}>
    <span>+</span> Upload Photos
  </button>
)}
              </div>

              <div className="ss-content-card">
                <div className="ss-card-header">
                  <h3 className="ss-card-title">Highlights / Badges</h3>
                  <p className="ss-card-sub">Select up to 3 highlights shown as colored chips on your customer card</p>
                </div>
                <div className="ss-badge-grid">
                  {allBadges.map((badge) => (
                    <button
                      key={badge.BadgeId}
                      className={`ss-badge-chip ${selectedBadges.includes(badge.BadgeId) ? 'selected' : ''}`}
                      onClick={() => toggleBadge(badge.BadgeId)}
                    >
                      {badge.Label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )
        )}

        {/* ══ FOR ALL LOCATIONS — HOLIDAY UPLIFT ══ */}
        {topTab === 'all' && allSubTab === 'holiday' && (
          isLoadingHoliday ? <HolidaySkeleton /> : holidays.length === 0 ? (
            <div className="service-settings-content" style={{ padding: 48, textAlign: 'center', color: '#64748b', fontFamily: '"DM Sans"', fontSize: 15 }}>
              No holidays configured for this service.
            </div>
          ) : (
            <>
              {/* ── Mobile holiday cards (shown via CSS on ≤768px) ── */}
              {renderMobileHoliday()}

              {/* ── Desktop holiday table (hidden via CSS on ≤768px) ── */}
              <div className="ss-holiday-desktop service-settings-content">
                <div className="service-settings-table-container">
                  <table className="service-settings-table service-settings-holiday-table">
                    <thead>
                      <tr><th>Date</th><th>Start From</th><th>End Time</th><th>Increase</th></tr>
                    </thead>
                    <tbody>
                      {holidays.map((h) => {
                        const isDirty =
                          h.startTime        !== h._origStart  ||
                          h.endTime          !== h._origEnd    ||
                          h.upliftPercentage !== h._origUplift ||
                          h.isActive         !== h._origActive;

                        return (
                          <tr key={h.holidayId} style={{ background: isDirty ? '#FFFBF0' : undefined }}>
                            <td className="service-settings-holiday-date">
                              <div className="service-settings-holiday-name">{h.name}</div>
                              <div className="service-settings-holiday-day">({h.dateDisplay})</div>
                            </td>
                            <td className="service-settings-holiday-time">
                              <TimeDropdown
                                value={h.startTime}
                                pickerId={`hol-${h.holidayId}-start`}
                                activeId={activeTimePicker}
                                onOpen={setActiveTimePicker}
                                onSelect={(val) => changeHolidayTime(h.holidayId, 'startTime', val)}
                                dropdownRef={timePickerRef}
                              />
                            </td>
                            <td className="service-settings-holiday-time">
                              <div className="service-settings-holiday-time-row">
                                <span className="service-settings-time-separator">To</span>
                                <TimeDropdown
                                  value={h.endTime}
                                  pickerId={`hol-${h.holidayId}-end`}
                                  activeId={activeTimePicker}
                                  onOpen={setActiveTimePicker}
                                  onSelect={(val) => changeHolidayTime(h.holidayId, 'endTime', val)}
                                  dropdownRef={timePickerRef}
                                />
                              </div>
                            </td>
                            <td className="service-settings-increase">
                              <div className="service-settings-increase-wrapper">
                                <input
                                  type="number"
                                  value={h.upliftPercentage}
                                  onChange={(e) => changeHolidayField(h.holidayId, 'upliftPercentage', e.target.value)}
                                  className="service-settings-increase-input"
                                />
                                <span className="service-settings-increase-percent">%</span>
                                <span className="service-settings-increase-icon">↑</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )
        )}

        {/* ══ FOR GARAGE — VARIANTS ══ */}
        {topTab === 'garage' && garageSubTab === 'variants' && (
          <>
            <div className="ss-warning-banner">
              <span className="ss-warning-icon">⚠️</span>These settings are specific to {baseName}
            </div>
            <div className="ss-content-card2">
              <h3 className="ss-card-title">Service Radius</h3>
              <p className="ss-card-sub" style={{ marginBottom: 20 }}>How far from {baseName} will you serve customers?</p>
               <div className="ss-slider-wrapper">
  <input
    type="range"
    min={1} max={25} step={0.5}
    value={radius}
    onChange={(e) => setRadius(Number(e.target.value))}
    className="ss-radius-slider"
    style={{
    background: `linear-gradient(180deg, #DED3FF 0%, #2020FF 100%) 0 0 / ${((radius - 1) / 24) * 100}% 100% no-repeat, #DCDAFF`
  }}
  />
  <img
  src={SliderPin}
  className="ss-slider-pin"
  alt=""
  style={{
    left: `calc(${((radius - 1) / 24) * 100}% + ${9 - ((radius - 1) / 24) * 18}px)`
  }}
/>
</div>
              <div className="ss-slider-labels"><span>{radius} miles</span><span>25 miles</span></div>
            </div>

            {isLoadingVariants ? <VariantsSkeleton /> : variants.length === 0 ? (
              <div className="service-settings-content" style={{ padding: 48, textAlign: 'center', color: '#64748b', fontFamily: '"DM Sans"', fontSize: 15 }}>
                No variants found for this service at this location.
              </div>
            ) : (
              <div className="service-settings-content">
                <div className="ss-variants-header">
                  <span className="ss-vth ss-vth-cat">Category</span>
                  <span className="ss-vth ss-vth-price">Price <span className="ss-vth-sub">(In USD)</span></span>
                  <span className="ss-vth ss-vth-dur">Duration <span className="ss-vth-sub">(In mins)</span></span>
                  <span className="ss-vth ss-vth-action">Action</span>
                </div>
                {variants.map((v) => {
                  const isExpanded = expandedId === v.id;
                  const included   = v.features.filter((f) =>  f.checked).length;
                  const excluded   = v.features.filter((f) => !f.checked).length;
                  return (
                    <div key={v.id} className="ss-variant-block">
                      <div className="ss-variant-row">
                        <div className="ss-variant-cat">
                          <label className="service-settings-toggle">
                            <input type="checkbox" checked={v.isActive} onChange={() => toggleVariant(v.id)} />
                            <span className="service-settings-toggle-slider" />
                          </label>
                          <div className="ss-variant-text">
                            <span className="ss-variant-name">{v.name}</span>
                            <span className="ss-variant-desc">{v.description}</span>
                          </div>
                        </div>
                        <div className="ss-variant-price-col" data-label="Price (USD)">
                          <input type="number" value={v.price} onChange={(e) => changePrice(v.id, e.target.value)} className="service-settings-price-input" />
                        </div>
                        <div className="ss-variant-dur-col" data-label="Duration (min)">
                          <input type="number" value={v.duration} onChange={(e) => changeDuration(v.id, e.target.value)} className="service-settings-price-input" placeholder="--"/>
                        </div>
                        <div className="ss-variant-action-col">
                          <button className="ss-expand-btn" onClick={() => toggleExpand(v.id)}>
                            {isExpanded
                              ? <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M15 12.5L10 7.5L5 12.5" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              : <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            }
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="ss-variant-expanded">
                          {v.features.length > 0 && (
                            <>
                              <div className="ss-feature-counts">
                                <span className="ss-count-included">{included} included</span>
                                <span className="ss-count-excluded">{excluded} Excluded</span>
                              </div>
                              <div className="ss-feature-grid">
                                {v.features.map((f) => (
                                  <div key={f.id} className={`ss-feature-item ${f.checked ? 'checked' : ''}`} onClick={() => toggleFeature(v.id, f.id)}>
                                    <div className={`ss-feature-checkbox ${f.checked ? 'checked' : ''}`}>
                                      {f.checked && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </div>
                                    <span className="ss-feature-label">{f.label}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          <div className="ss-facilities">
                            <div className="ss-facilities-title">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="#6366F1" strokeWidth="1.6"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="#6366F1" strokeWidth="1.4"/></svg>
                              <span>Other Facilities</span>
                            </div>
                            <div className="ss-facilities-row">
                              <div className="ss-facility-group">
                                <label className="ss-facility-label">Warranty</label>
                                <div className="ss-select-wrap">
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7L12 2z" stroke="#64748b" strokeWidth="1.6" fill="none"/></svg>
                                  <select value={v.warranty} onChange={(e) => changeWarranty(v.id, e.target.value)} className="ss-select">
                                    <option value="">Choose</option>
                                    {warrantyOptions.length > 0
                                      ? warrantyOptions.map((w) => <option key={w.Value} value={w.Value}>{w.Label}</option>)
                                      : <><option value="1_week">1 Week</option><option value="1_month">1 Month</option><option value="3_months">3 Months</option><option value="6_months">6 Months</option><option value="1_year">1 Year</option></>
                                    }
                                  </select>
                                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/></svg>
                                </div>
                              </div>
                              <div className="ss-facility-group">
                                <label className="ss-facility-label">Pickup &amp; Drop</label>
                                <div className="ss-radio-group">
                                  <label className={`ss-radio-opt ${v.pickup === 'yes' ? 'selected' : ''}`} onClick={() => changePickup(v.id, 'yes')}>
                                    <div className={`ss-radio-dot ${v.pickup === 'yes' ? 'selected' : ''}`} />Yes, Available
                                  </label>
                                  <label className={`ss-radio-opt ${v.pickup === 'no' ? 'selected' : ''}`} onClick={() => changePickup(v.id, 'no')}>
                                    <div className={`ss-radio-dot ${v.pickup === 'no' ? 'selected' : ''}`} />No, Not Available
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ══ FOR GARAGE — AVAILABILITY ══ */}
        {topTab === 'garage' && garageSubTab === 'availability' && (
          <>
            <div className="ss-warning-banner">
              <span className="ss-warning-icon">⚠️</span>These settings are specific to {baseName}
            </div>

            {isLoadingAvailability ? <AvailabilitySkeleton /> : (
              <>
                {/* ── Mobile availability cards (shown via CSS on ≤768px) ── */}
                {renderMobileAvailability()}

                {/* ── Desktop availability table (hidden via CSS on ≤768px) ── */}
                <div className="ss-avail-desktop service-settings-content">
                  <div className="service-settings-table-container">
                    <table className="service-settings-table service-settings-availability-table">
                      <thead>
                        <tr><th>Day</th><th>Available From</th><th>Available To</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {availability.map((day) => (
                          <tr key={day.id}>
                            <td className="service-settings-day">
                              <span className="service-settings-day-name">{day.day}</span>
                            </td>
                            <td className="service-settings-time-from">
                              {day.slots.map((slot, idx) => (
                                <div key={idx} className="service-settings-time-row">
                                  <TimeDropdown
                                    value={slot.from}
                                    pickerId={`avail-${day.id}-${idx}-from`}
                                    activeId={activeTimePicker}
                                    onOpen={setActiveTimePicker}
                                    onSelect={(val) => setSlotTime(day.id, idx, 'from', val)}
                                    dropdownRef={timePickerRef}
                                  />
                                </div>
                              ))}
                            </td>
                            <td className="service-settings-time-to">
                              {day.slots.map((slot, idx) => (
                                <div key={idx} className="service-settings-time-row">
                                  <span className="service-settings-time-separator">To</span>
                                  <TimeDropdown
                                    value={slot.to}
                                    pickerId={`avail-${day.id}-${idx}-to`}
                                    activeId={activeTimePicker}
                                    onOpen={setActiveTimePicker}
                                    onSelect={(val) => setSlotTime(day.id, idx, 'to', val)}
                                    dropdownRef={timePickerRef}
                                  />
                                  {idx === day.slots.length - 1
                                    ? <button className="service-settings-add-slot-btn" onClick={() => addSlot(day.id)}>+</button>
                                    : <button className="service-settings-remove-slot-btn" onClick={() => removeSlot(day.id, idx)}>×</button>
                                  }
                                </div>
                              ))}
                            </td>
                            <td className="service-settings-status">
                              <label className="service-settings-toggle">
                                <input type="checkbox" checked={day.isActive} onChange={() => toggleAvailDay(day.id)} />
                                <span className="service-settings-toggle-slider" />
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Bottom Bar */}
        <div className="ss-bottom-bar">
          <button className="ss-cancel-btn" onClick={() => navigate(-1)}>CANCEL</button>
          <button className="ss-save-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'SAVE'}
          </button>
        </div>

      </Container>

      {deleteImageTarget && (
        <DeleteImageModal
          onConfirm={handleDeleteImage}
          onCancel={() => setDeleteImageTarget(null)}
          isLoading={isDeletingImage}
        />
      )}
    </div>
  );
};

export default ServiceSettings;